import { type SxProps, type Theme } from '@mui/material';

import { themeColors } from '@/config/theme/themeColors.ts';

export const FallbackUIMainStyles: SxProps<Theme> = {
  '&::after': {
    backgroundImage: `linear-gradient(${themeColors.turquoiseLight}55 1px, transparent 1px), linear-gradient(90deg, ${themeColors.turquoiseLight}55 1px, transparent 1px)`,
    backgroundSize: '24px 24px',
    bottom: 0,
    content: '""',
    height: '42%',
    left: 0,
    maskImage: 'linear-gradient(to bottom, transparent, black)',
    opacity: 0.38,
    position: 'absolute',
    width: '38%',
  },
  '&::before': {
    border: `1px solid ${themeColors.turquoiseLight}`,
    borderRadius: '50%',
    content: '""',
    height: 680,
    position: 'absolute',
    right: -290,
    top: -350,
    width: 680,
  },
  alignItems: 'center',
  background: `radial-gradient(circle at 8% 0%, rgba(21, 190, 240, 0.16), transparent 28rem), linear-gradient(145deg, ${themeColors.bg} 0%, ${themeColors.white} 58%, #eaf8fd 100%)`,
  boxSizing: 'border-box',
  display: 'flex',
  minHeight: '100dvh',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
};

export const FallbackUIContainerStyles: SxProps<Theme> = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: '100dvh',
  paddingBottom: { sm: 6, xs: 4 },
  paddingTop: { sm: 6, xs: 4 },
  position: 'relative',
  zIndex: 1,
};

export const FallbackUILogoStyles: SxProps<Theme> = {
  alignSelf: 'flex-start',
  height: { sm: 32, xs: 28 },
  marginBottom: { sm: 5, xs: 4 },
  overflow: 'hidden',
  position: 'relative',
  width: { sm: 180, xs: 160 },
};

export const FallbackUILogoImageStyles: SxProps<Theme> = {
  height: { sm: 62, xs: 54 },
  left: 0,
  maxWidth: 'none',
  position: 'absolute',
  top: { sm: -15, xs: -13 },
  width: 'auto',
};

export const FallbackUICardStyles: SxProps<Theme> = {
  backgroundColor: themeColors.white,
  border: `1px solid ${themeColors.silverTwo}`,
  borderRadius: { sm: 4, xs: 3 },
  boxShadow: '0 28px 80px rgba(0, 87, 184, 0.14)',
  display: 'grid',
  gridTemplateColumns: { md: 'minmax(240px, 0.72fr) minmax(0, 1.28fr)', xs: '1fr' },
  overflow: 'hidden',
};

export const FallbackUIIllustrationStyles: SxProps<Theme> = {
  '&::after': {
    background:
      'linear-gradient(135deg, transparent 49%, rgba(255, 255, 255, 0.16) 50%, transparent 51%)',
    backgroundSize: '18px 18px',
    bottom: 0,
    content: '""',
    height: 120,
    left: 0,
    opacity: 0.7,
    position: 'absolute',
    width: '100%',
  },
  '&::before': {
    border: '1px solid rgba(255, 255, 255, 0.24)',
    borderRadius: '50%',
    content: '""',
    height: 330,
    position: 'absolute',
    right: -165,
    top: -155,
    width: 330,
  },
  alignItems: 'center',
  background: `linear-gradient(150deg, ${themeColors.darkBlue} 0%, ${themeColors.blue} 68%, ${themeColors.turquoiseDark} 120%)`,
  color: themeColors.white,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: { md: 460, xs: 220 },
  overflow: 'hidden',
  position: 'relative',
};

export const FallbackUIIconBadgeStyles: SxProps<Theme> = {
  backgroundColor: themeColors.white,
  borderRadius: '50%',
  boxShadow: '0 18px 44px rgba(0, 35, 91, 0.28)',
  display: 'grid',
  height: { sm: 132, xs: 120 },
  placeItems: 'center',
  position: 'relative',
  transform: 'rotate(-7deg)',
  width: { sm: 132, xs: 120 },
  zIndex: 1,
};

export const FallbackUIIllustrationLabelStyles: SxProps<Theme> = {
  color: 'rgba(255, 255, 255, 0.78)',
  fontSize: 11,
  letterSpacing: '0.14em',
  marginTop: 3,
  position: 'relative',
  textTransform: 'uppercase',
  zIndex: 1,
};

export const FallbackUIContentStyles: SxProps<Theme> = {
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: { md: 7, sm: 6, xs: 4 },
};

export const FallbackUIEyebrowStyles: SxProps<Theme> = {
  color: themeColors.turquoiseDark,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.15em',
  marginBottom: 1.5,
  textTransform: 'uppercase',
};

export const FallbackUITitleStyles: SxProps<Theme> = {
  color: themeColors.darkBlue,
  fontSize: { sm: '3.4rem', xs: '2.45rem' },
  fontWeight: 700,
  letterSpacing: '-0.06em',
  lineHeight: 0.98,
  marginBottom: 2.5,
};

export const FallbackUIMessageStyles: SxProps<Theme> = {
  color: themeColors.darkGrey,
  fontSize: { sm: '1.1rem', xs: '1rem' },
  lineHeight: 1.65,
  maxWidth: 460,
};

export const FallbackUIReportStyles: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: themeColors.gray10,
  borderLeft: '3px solid',
  borderRadius: 2,
  boxSizing: 'border-box',
  color: themeColors.darkGrey,
  padding: 2,
  width: '100%',
};

export const FallbackUIReportIconStyles: SxProps<Theme> = {
  alignItems: 'center',
  backgroundColor: themeColors.white,
  borderRadius: '50%',
  display: 'grid',
  flexShrink: 0,
  height: 36,
  placeItems: 'center',
  width: 36,
};

export const FallbackUIActionStyles: SxProps<Theme> = {
  '&:hover': {
    boxShadow: '0 13px 26px rgba(0, 87, 184, 0.3)',
  },
  borderRadius: 999,
  boxShadow: '0 10px 22px rgba(0, 87, 184, 0.22)',
  fontWeight: 700,
  minHeight: 48,
  paddingLeft: 3,
  paddingRight: 3,
};

export const FallbackUIFooterStyles: SxProps<Theme> = {
  color: themeColors.steelGrey,
  fontSize: 12,
  letterSpacing: '0.04em',
  marginTop: 3,
};
